import React, { useMemo, useState } from 'react'
import { noop } from 'lodash-es'
import { CollapseContentWrapper, SummaryCollapsePanel, TicketFields } from 'view/shared/ticket'
import { CollapsePanel } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField, TextInputField } from 'view/components/inputs'
import {
  AxeTradeConditionButtonsField,
  DealCounterpartyComboboxField,
  DealFundComboboxField,
  DealSponsorComboboxField,
  FilteredTermSelectboxField,
  MasterLegalAgreementTypeSelectboxField,
  OrganisationDecisionMakerSelectboxField,
  PrimeBrokerSelectboxField,
  TradingDatePickerField
} from 'view/shared/entity'
import { HaircutTypeSelectboxField } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import { ToggleField } from 'view/components/toggle'
import { getCollateralMarket, getDateEditorFormat, ReferenceRateHelper, RepoClassificationHelper, UserHelper } from 'model'
import { Locale, Translation } from 'locale'
import {
  DealDetail,
  Market,
  NegotiationFieldMeta,
  RepoClassification,
  ServicingType,
  TradingEntityClearingType,
  TradingType,
  UserPreference
} from 'api'
import { DealFieldNames } from '../DealsGridColumnNames'
import {
  formatRepoType,
  getCusip,
  getExecutingTradingEntityName,
  getExecutionDecisionMaker,
  getExecutionDecisionMakerName,
  getFeeFormatOptions,
  getFormattedDirection,
  getHaircutFormatOptions,
  getInvestmentDecisionMaker,
  getInvestmentDecisionMakerName,
  getIsin,
  getNotionalFormatOptions,
  getOrganisationField,
  getRateFormatOptions,
  getServicingDealExecutionDecisionMakerId,
  getServicingDealInvestmentDecisionMakerId,
  getServicingDealTradingEntityField,
  getSettlementVenueName,
  getSponsorName,
  getTradingEntityField,
  getTradingEntityLei,
  getTradingEntityName
} from 'view/shared/datagrid'
import { getTrader } from '../dealsFormatters'
import { canServicingUpdateAttributes, getDealModificationColour, getNegotiatedValue, isClearedDeal } from '../utils'
import * as constants from 'view/styles/constants'
import { LabelPosition } from 'view/styles/constants'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import { ClearingHouseDealTicketPanel } from './ClearingHouseDealTicketPanel'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'
import {
  getBondPriceModificationType,
  getEndDateModificationType,
  getHaircutModificationType,
  getNotionalModificationType,
  getRateModificationType,
  getStartDateModificationType,
  getStringModificationType,
  getTermModificationType
} from 'view/routes/requests/shared/deltaGetters'
import { CallableTypeSelectboxField } from 'view/shared/entity/CallableTypeSelectbox'
import { isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { getDealIndexRatio, isDealDetailGcPlus, isDealDetailSBL } from 'model/dealUtils'
import { ServicingSecondaryCollateralComboboxField } from 'view/shared/entity/SecondaryCollateralSelectbox'
import { addDays, parseISO, subDays, toDate } from 'date-fns'
import { toLocalDateString } from 'utils'

interface DealTicketFieldsProps {
  dealDetail: DealDetail
  userPreference: UserPreference
  isAdminUser: boolean
  onUpdateField: (value: any, field?: NegotiationFieldMeta, rawValue?: any) => unknown
  isAdminAmendAndWithOtherUser: boolean
  isSBLEnabled: boolean
}

const mapStateToProps = createStructuredSelector({
  isAdminUser: isAdminGatewayUserSelector,
  isSBLEnabled: isStockBorrowingLendingEnabledSelector
})

export const DealTicketSponsorField: React.FunctionComponent<{
  dealDetail: DealDetail
  isAdminUser: boolean
  editable: boolean
  onUpdateField: (value: any, field?: NegotiationFieldMeta, rawValue?: any) => unknown
}> = props => {
  const { dealDetail, editable, isAdminUser, onUpdateField } = props
  const { deal, servicingSideDetails, ownOrder } = dealDetail
  const negotiationFields = servicingSideDetails?.negotiationFields

  const isAdminAmend = isAdminUser && deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND
  const validationIssues = isAdminAmend ? servicingSideDetails?.validationIssues ?? undefined : undefined
  const sponsorEditable = editable && ownOrder && deal.clearingType === TradingEntityClearingType.SPONSORED && deal.market !== Market.US

  return (
    <DialogField>
      {sponsorEditable ? (
        <DealSponsorComboboxField
          fundId={deal.fundId}
          clearingHouseId={deal.clearingHouseId}
          label={Locale.translate(Translation.TC_SPONSORING_BANK)}
          fieldName={NegotiationFieldMeta.SPONSOR_ID}
          validationField={DealFieldNames.DEAL_SPONSOR_ID}
          validationIssues={validationIssues}
          underline={getDealModificationColour(
            () => getStringModificationType(negotiationFields?.SPONSOR_ID?.negotiableFieldRequired),
            dealDetail
          )}
          value={isAdminAmend ? getNegotiatedValue(negotiationFields?.SPONSOR_ID?.negotiableFieldRequired) : deal.sponsorId}
          readOnly={false}
          onUpdate={onUpdateField}
        />
      ) : (
        <TextInputField
          label={Locale.translate(Translation.TC_SPONSORING_BANK)}
          readOnly={true}
          onUpdate={noop}
          value={getSponsorName(dealDetail, true)}
        />
      )}
    </DialogField>
  )
}

export const DealTicketExecutingCounterpartySponsorField: React.FunctionComponent<{
  dealDetail: DealDetail
}> = props => {
  const { dealDetail } = props

  return (
    <DialogField>
      <TextInputField
        label={Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY_SPONSOR_SHORT)}
        readOnly={true}
        onUpdate={noop}
        value={getSponsorName(dealDetail, false)}
      />
    </DialogField>
  )
}

export const DealTicketInvestmentDecisionMakerField: React.FunctionComponent<{
  dealDetail: DealDetail
  self: boolean
  isAdminUser: boolean
  readonly: boolean
  onUpdateField: (value: any, field?: NegotiationFieldMeta, rawValue?: any) => unknown
}> = props => {
  const { dealDetail, self, isAdminUser, readonly, onUpdateField } = props
  const { ownOrder, deal, counterpartyFirm, orderFundFirm, servicingSideDetails } = dealDetail
  const negotiationFields = servicingSideDetails?.negotiationFields
  const isAdminAmend = isAdminUser && deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND

  const label = self
    ? Locale.translate(Translation.TC_MY_INVESTMENT_DECISION_MAKER)
    : Locale.translate(Translation.TC_COUNTERPARTY_INVESTMENT_DECISION_MAKER)
  const useInitiatorSide = (ownOrder && self) || (!ownOrder && !self)
  const fieldName = useInitiatorSide
    ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
    : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER
  const organisation = useInitiatorSide ? orderFundFirm : counterpartyFirm
  const organisationId = organisation?.id ?? null

  return (
    <DialogField>
      {isAdminUser ? (
        <OrganisationDecisionMakerSelectboxField
          label={label}
          fieldName={fieldName}
          organisationId={organisationId}
          underline={getDealModificationColour(
            () => getStringModificationType(negotiationFields?.[fieldName]?.negotiableFieldRequired),
            dealDetail
          )}
          value={
            isAdminAmend ? getServicingDealInvestmentDecisionMakerId(dealDetail, self) : getInvestmentDecisionMaker(dealDetail, self)?.id
          }
          readOnly={readonly}
          onUpdate={onUpdateField}
        />
      ) : (
        <TextInputField label={label} readOnly={true} onUpdate={noop} value={getInvestmentDecisionMakerName(dealDetail, self)} />
      )}
    </DialogField>
  )
}

export const DealTicketExecutionDecisionMakerField: React.FunctionComponent<{
  dealDetail: DealDetail
  self: boolean
  isAdminUser: boolean
  readonly: boolean
  onUpdateField: (value: any, field?: NegotiationFieldMeta, rawValue?: any) => unknown
}> = props => {
  const { dealDetail, self, isAdminUser, readonly, onUpdateField } = props
  const { ownOrder, deal, counterpartyFirm, orderFundFirm, servicingSideDetails } = dealDetail
  const negotiationFields = servicingSideDetails?.negotiationFields
  const isAdminAmend = isAdminUser && deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND

  const label = self
    ? Locale.translate(Translation.TC_MY_EXECUTION_DECISION_MAKER)
    : Locale.translate(Translation.TC_COUNTERPARTY_EXECUTION_DECISION_MAKER)
  const useInitiatorSide = (ownOrder && self) || (!ownOrder && !self)
  const fieldName = useInitiatorSide
    ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
    : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER
  const organisation = useInitiatorSide ? orderFundFirm : counterpartyFirm
  const organisationId = organisation?.id ?? null

  return (
    <DialogField>
      {isAdminUser ? (
        <OrganisationDecisionMakerSelectboxField
          label={label}
          fieldName={fieldName}
          organisationId={organisationId}
          underline={getDealModificationColour(
            () => getStringModificationType(negotiationFields?.[fieldName]?.negotiableFieldRequired),
            dealDetail
          )}
          value={
            isAdminAmend ? getServicingDealExecutionDecisionMakerId(dealDetail, self) : getExecutionDecisionMaker(dealDetail, self)?.id
          }
          readOnly={readonly}
          onUpdate={onUpdateField}
        />
      ) : (
        <TextInputField label={label} readOnly={true} onUpdate={noop} value={getExecutionDecisionMakerName(dealDetail, self)} />
      )}
    </DialogField>
  )
}

const DealTicketFieldsComponent: React.FunctionComponent<DealTicketFieldsProps> = props => {
  const [startDate, setStartDate] = useState(new Date())
  const { dealDetail, userPreference, isAdminUser, onUpdateField, isAdminAmendAndWithOtherUser, isSBLEnabled } = props
  const {
    deal,
    servicingSideDetails,
    collateral,
    collateralBasket,
    orderFund,
    counterparty,
    product,
    productSector,
    referenceRate,
    ownOrder
  } = dealDetail
  const negotiationFields = servicingSideDetails?.negotiationFields
  const currency = dealDetail.deal.currency
  const market = collateral && getCollateralMarket(collateral)

  const isAdminAmend = isAdminUser && deal.servicingDetails?.servicingType.servicingType === ServicingType.SERVICING_AMEND
  const validationIssues = isAdminAmend ? servicingSideDetails?.validationIssues ?? undefined : undefined

  const usSponsoredClearing = deal.market === Market.US && deal.clearingType === TradingEntityClearingType.SPONSORED

  const notionalFormatOptions = useMemo(() => getNotionalFormatOptions(userPreference.amountFormat), [userPreference])
  const dateEditorFormat = useMemo(() => getDateEditorFormat(userPreference.dateFormat), [userPreference])

  const editable = canServicingUpdateAttributes(dealDetail) && isAdminAmend && !isAdminAmendAndWithOtherUser
  const cleared = isClearedDeal(dealDetail)

  const term = isAdminAmend ? getNegotiatedValue(negotiationFields?.TERM?.negotiableFieldRequired) : deal.term
  const bookField =
    negotiationFields?.[ownOrder ? NegotiationFieldMeta.INITIATOR_BOOK : NegotiationFieldMeta.RESPONDER_BOOK]?.negotiableFieldRequired
  const strategyField =
    negotiationFields?.[ownOrder ? NegotiationFieldMeta.INITIATOR_STRATEGY : NegotiationFieldMeta.RESPONDER_STRATEGY]
      ?.negotiableFieldRequired

  const dealIsNotSbl = !isDealDetailSBL(dealDetail)
  const dealIsGcPlus = isDealDetailGcPlus(dealDetail)

  const startDatePlusOneDay = addDays(toDate(startDate), 1)
  const collateralMaturity = collateral?.couponMaturityDate ? toLocalDateString(collateral.couponMaturityDate) : undefined
  const collateralMaturityMinusOneDay = collateralMaturity ? subDays(parseISO(collateralMaturity), 1) : undefined

  return (
    <TicketFields>
      <SummaryCollapsePanel title={Locale.translate(Translation.TC_TICKET_QUICK_SUMMARY)} defaultCollapsed={false}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                fieldName={NegotiationFieldMeta.NOTIONAL}
                validationField={DealFieldNames.DEAL_NOTIONAL}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getNotionalModificationType(negotiationFields?.NOTIONAL?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || deal.tradingType === TradingType.CASH}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                formatOptions={notionalFormatOptions}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.NOTIONAL?.negotiableFieldRequired) : deal.notional}
                dataTestId={'DealTicketFields-NumericInputField-Notional'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE_VS_REFERENCE_RATE, {
                  referenceRate: referenceRate ? ReferenceRateHelper.getName(referenceRate) : null
                })}
                fieldName={NegotiationFieldMeta.RATE}
                validationField={DealFieldNames.DEAL_RATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getRateModificationType(negotiationFields?.RATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || !dealIsNotSbl}
                formatOptions={getRateFormatOptions(referenceRate)}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.RATE?.negotiableFieldRequired) : deal.rate}
                dataTestId={'DealTicketFields-NumericInputField-Rate-ReferenceRate'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT)}
                fieldName={NegotiationFieldMeta.HAIRCUT}
                validationField={DealFieldNames.DEAL_HAIRCUT}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getHaircutModificationType(negotiationFields?.HAIRCUT?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || isClearedDeal(dealDetail) || !dealIsNotSbl || dealIsGcPlus}
                min={0}
                formatOptions={getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, deal.direction)}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.HAIRCUT?.negotiableFieldRequired) : deal.haircut}
                dataTestId={'DealTicketFields-NumericInputField-Haircut'}
              />
            </DialogField>
            <DialogField>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_HAIRCUT_TYPE)}
                fieldName={NegotiationFieldMeta.HAIRCUT_TYPE}
                validationField={DealFieldNames.DEAL_HAIRCUT}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStringModificationType(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || !dealIsNotSbl || dealIsGcPlus}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired) : deal.haircutType}
                dataTestId={'DealTicketFields-NumericInputField-HaircutType'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_START_DATE)}
                market={market}
                fieldName={NegotiationFieldMeta.START_DATE}
                validationField={DealFieldNames.DEAL_START_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStartDateModificationType(negotiationFields?.START_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable}
                dateFormat={dateEditorFormat}
                onUpdate={value => {
                  setStartDate(value)
                  onUpdateField(value, NegotiationFieldMeta.START_DATE)
                }}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.START_DATE?.negotiableFieldRequired) : deal.startDate}
                dataTestId={'DealTicketFields-NumericInputField-StartDate'}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_END_DATE)}
                fieldName={NegotiationFieldMeta.END_DATE}
                validationField={DealFieldNames.DEAL_END_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getEndDateModificationType(negotiationFields?.END_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || (deal.repoClassification === RepoClassification.OPEN && !deal.endDate)}
                dateFormat={dateEditorFormat}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.END_DATE?.negotiableFieldRequired) : deal.endDate}
                dataTestId={'DealTicketFields-NumericInputField-EndDate'}
                minDate={startDatePlusOneDay}
                maxDate={collateralMaturityMinusOneDay}
              />
            </DialogField>
            <DialogField>
              <FilteredTermSelectboxField
                label={Locale.translate(Translation.TC_TERM)}
                fieldName={NegotiationFieldMeta.TERM}
                validationField={DealFieldNames.DEAL_TERM}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getTermModificationType(negotiationFields?.TERM?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable}
                onUpdate={onUpdateField}
                repoClassification={deal.endDate ? RepoClassification.FIXED : deal.repoClassification}
                value={term}
                dataTestId={'DealTicketFields-NumericInputField-Term'}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
                fieldName={NegotiationFieldMeta.BOND_PRICE}
                validationField={DealFieldNames.DEAL_BOND_PRICE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getBondPriceModificationType(negotiationFields?.BOND_PRICE?.negotiableFieldRequired),
                  dealDetail
                )}
                min={0}
                maxLength={constants.bondPriceMaxFieldLength}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                readOnly={!editable || !!deal.basketAllocation}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.BOND_PRICE?.negotiableFieldRequired) : deal.bondPrice}
                dataTestId={'DealTicketFields-NumericInputField-CollateralPrice'}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={
                  isAdminAmend
                    ? getNegotiatedValue(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired)
                    : deal.startConsideration
                }
              />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CURRENCY)} readOnly={true} onUpdate={noop} value={currency} />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_DIRECTION)}
                fieldName={DealFieldNames.DEAL_DIRECTION}
                readOnly={true}
                onUpdate={noop}
                value={getFormattedDirection(deal.direction, ownOrder, userPreference.directionFormat)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_ISIN_BUI)}
                fieldName={DealFieldNames.DEAL_COLLATERAL_ISIN}
                readOnly={true}
                onUpdate={noop}
                value={getIsin(dealDetail)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                dataTestId={'DealTicketFields-NumericInputField-Collateral'}
                label={Locale.translate(Translation.TC_COLLATERAL)}
                readOnly={true}
                onUpdate={noop}
                value={collateral?.name ?? collateralBasket?.name}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_SETTLEMENT)}
                readOnly={true}
                onUpdate={noop}
                value={getSettlementVenueName(deal.settlementVenue)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_TRADE_TYPE)}
                readOnly={true}
                onUpdate={noop}
                value={RepoClassificationHelper.getName(deal.repoClassification)}
              />
            </DialogField>
          </DialogRow>
          {isSBLEnabled ? (
            <DialogRow>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_REPO_TYPE)}
                  readOnly={true}
                  onUpdate={noop}
                  value={formatRepoType(deal.repoType)}
                />
              </DialogField>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_STOCK_BORROW_LENDING_TYPE)}
                  readOnly={true}
                  onUpdate={noop}
                  value={
                    isAdminAmend
                      ? getNegotiatedValue(negotiationFields?.STOCK_BORROWING_LENDING_TYPE?.negotiableFieldRequired)
                      : dealDetail.deal.stockBorrowingLendingType
                  }
                />
              </DialogField>

              <DialogField>
                <NumericInputField
                  label={Locale.translate(Translation.TC_FEE)}
                  fieldName={NegotiationFieldMeta.FEE}
                  validationField={DealFieldNames.DEAL_FEE}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getRateModificationType(negotiationFields?.FEE?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable || dealIsNotSbl}
                  formatOptions={getFeeFormatOptions()}
                  onUpdate={onUpdateField}
                  value={isAdminAmend ? getNegotiatedValue(negotiationFields?.FEE?.negotiableFieldRequired) : deal.fee}
                  dataTestId={'DealTicketFields-NumericInputField-Fee'}
                />
              </DialogField>

              <DialogField>
                <ServicingSecondaryCollateralComboboxField
                  detail={dealDetail}
                  fieldName={NegotiationFieldMeta.SECONDARY_BASKET_ID}
                  label={Locale.translate(Translation.TC_SECONDARY_COLLATERAL)}
                  onUpdate={onUpdateField}
                  underline={getDealModificationColour(
                    () => getTermModificationType(negotiationFields?.SECONDARY_BASKET_ID?.negotiableFieldRequired),
                    dealDetail
                  )}
                  validationIssues={validationIssues}
                  validationField={DealFieldNames.DEAL_SECONDARY_COLLATERAL}
                  readOnly={!editable || dealIsNotSbl}
                  value={
                    isAdminAmend
                      ? getNegotiatedValue(negotiationFields?.SECONDARY_BASKET_ID?.negotiableFieldRequired)
                      : deal.secondaryBasketId
                  }
                />
              </DialogField>
            </DialogRow>
          ) : null}
          <DialogRow>
            {cleared ? (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getTradingEntityName(dealDetail, false)}
                />
              </DialogField>
            ) : (
              <DialogField>
                {ownOrder ? (
                  <DealCounterpartyComboboxField
                    organisationId={counterparty?.organisationId ?? null}
                    isin={deal.collateralId}
                    fundId={deal.fundId}
                    marketType={deal.marketType}
                    usSponsoredClearing={usSponsoredClearing}
                    showAlias={true}
                    label={Locale.translate(Translation.TC_COUNTERPARTY)}
                    fieldName={NegotiationFieldMeta.COUNTERPARTY_ID}
                    validationField={DealFieldNames.DEAL_COUNTERPARTY_ID}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(negotiationFields?.COUNTERPARTY_ID?.negotiableFieldRequired),
                      dealDetail
                    )}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    value={
                      isAdminAmend ? getNegotiatedValue(negotiationFields?.COUNTERPARTY_ID?.negotiableFieldRequired) : counterparty?.id
                    }
                  />
                ) : (
                  <DealFundComboboxField
                    organisationId={orderFund?.organisationId ?? null}
                    isin={deal.collateralId}
                    marketType={deal.marketType}
                    counterpartyId={deal.counterpartyId}
                    usSponsoredClearing={usSponsoredClearing}
                    showAlias={true}
                    label={Locale.translate(Translation.TC_COUNTERPARTY)}
                    fieldName={NegotiationFieldMeta.FUND_ID}
                    validationField={DealFieldNames.DEAL_FUND_ID}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(negotiationFields?.FUND_ID?.negotiableFieldRequired),
                      dealDetail
                    )}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    value={isAdminAmend ? getNegotiatedValue(negotiationFields?.FUND_ID?.negotiableFieldRequired) : orderFund?.id}
                  />
                )}
              </DialogField>
            )}
            {cleared ? (
              <DealTicketSponsorField editable={editable} dealDetail={dealDetail} isAdminUser={isAdminUser} onUpdateField={onUpdateField} />
            ) : (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_CONTACT)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getTrader(dealDetail.deal.traderUserId, dealDetail.deal.counterpartyTraderId, ownOrder, false)}
                />
              </DialogField>
            )}
            <DialogField>
              <MasterLegalAgreementTypeSelectboxField
                label={Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT)}
                value={deal.masterLegalAgreementType}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
          {cleared ? (
            <DialogRow>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getExecutingTradingEntityName(dealDetail)}
                />
              </DialogField>
              {isAdminUser && <DealTicketExecutingCounterpartySponsorField dealDetail={dealDetail} />}
              <DialogField>
                <ToggleField
                  label={Locale.translate(Translation.TC_PRE_AGREED)}
                  fieldName={NegotiationFieldMeta.PRE_AGREED}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.PRE_AGREED?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable}
                  onUpdate={onUpdateField}
                  checked={isAdminAmend ? getNegotiatedValue(negotiationFields?.PRE_AGREED?.negotiableFieldRequired) : deal.preAgreed}
                  labelPosition={LabelPosition.Top}
                />
              </DialogField>
              {!isAdminUser && <DialogField />}
            </DialogRow>
          ) : (
            <>
              <DialogRow>
                <DialogField>
                  <TextInputField
                    label={Locale.translate(Translation.TC_BOOK)}
                    fieldName={ownOrder ? NegotiationFieldMeta.INITIATOR_BOOK : NegotiationFieldMeta.RESPONDER_BOOK}
                    validationField={ownOrder ? DealFieldNames.DEAL_INITIATOR_BOOK : DealFieldNames.DEAL_RESPONDER_BOOK}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(() => getStringModificationType(bookField), dealDetail)}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    value={isAdminAmend ? getNegotiatedValue(bookField) : ownOrder ? deal.initiatorBook : deal.responderBook}
                    maxLength={constants.internalBookingMaxFieldLength}
                  />
                </DialogField>
                <DialogField>
                  <TextInputField
                    label={Locale.translate(Translation.TC_STRATEGY)}
                    fieldName={ownOrder ? NegotiationFieldMeta.INITIATOR_STRATEGY : NegotiationFieldMeta.RESPONDER_STRATEGY}
                    validationField={ownOrder ? DealFieldNames.DEAL_INITIATOR_STRATEGY : DealFieldNames.DEAL_RESPONDER_STRATEGY}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(() => getStringModificationType(strategyField), dealDetail)}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    value={isAdminAmend ? getNegotiatedValue(strategyField) : ownOrder ? deal.initiatorStrategy : deal.responderStrategy}
                    maxLength={constants.internalBookingMaxFieldLength}
                  />
                </DialogField>
                <DialogField>
                  {!ownOrder ? (
                    <TextInputField
                      label={Locale.translate(Translation.TC_PRIME_BROKER)}
                      readOnly={true}
                      onUpdate={noop}
                      value={deal.initiatorPrimeBrokerName}
                    />
                  ) : (
                    <PrimeBrokerSelectboxField
                      organisationId={deal.initiatorOrganisationId}
                      label={Locale.translate(Translation.TC_PRIME_BROKER)}
                      fieldName={NegotiationFieldMeta.INITIATOR_PRIME_BROKER_ID}
                      validationField={DealFieldNames.DEAL_INITIATOR_PRIME_BROKER_ID}
                      validationIssues={validationIssues}
                      underline={getDealModificationColour(
                        () => getStringModificationType(negotiationFields?.INITIATOR_PRIME_BROKER_ID?.negotiableFieldRequired),
                        dealDetail
                      )}
                      readOnly={!editable}
                      onUpdate={onUpdateField}
                      value={
                        isAdminAmend
                          ? getNegotiatedValue(negotiationFields?.INITIATOR_PRIME_BROKER_ID?.negotiableFieldRequired)
                          : deal.initiatorPrimeBrokerId
                      }
                    />
                  )}
                </DialogField>
              </DialogRow>
              <DialogRow>
                <DialogField>
                  <TextInputField
                    label={Locale.translate(Translation.TC_BUNDLE)}
                    fieldName={NegotiationFieldMeta.BUNDLE}
                    validationField={DealFieldNames.DEAL_BUNDLE}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(negotiationFields?.BUNDLE?.negotiableFieldRequired),
                      dealDetail
                    )}
                    readOnly={ownOrder ? !editable : true}
                    onUpdate={ownOrder ? onUpdateField : noop}
                    value={isAdminAmend ? getNegotiatedValue(negotiationFields?.BUNDLE?.negotiableFieldRequired) : deal.bundle}
                    maxLength={constants.internalBookingMaxFieldLength}
                  />
                </DialogField>
                <DialogField>
                  <ToggleField
                    label={Locale.translate(Translation.TC_PRE_AGREED)}
                    fieldName={NegotiationFieldMeta.PRE_AGREED}
                    underline={getDealModificationColour(
                      () => getStringModificationType(negotiationFields?.PRE_AGREED?.negotiableFieldRequired),
                      dealDetail
                    )}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    checked={isAdminAmend ? getNegotiatedValue(negotiationFields?.PRE_AGREED?.negotiableFieldRequired) : deal.preAgreed}
                    labelPosition={LabelPosition.Top}
                  />
                </DialogField>
                <DialogField />
              </DialogRow>
            </>
          )}
        </CollapseContentWrapper>
      </SummaryCollapsePanel>
      {isAdminUser && cleared && <ClearingHouseDealTicketPanel dealDetail={dealDetail} />}
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_AMOUNT)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
                fieldName={NegotiationFieldMeta.NOTIONAL}
                validationField={DealFieldNames.DEAL_NOTIONAL}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getNotionalModificationType(negotiationFields?.NOTIONAL?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || deal.tradingType === TradingType.CASH}
                min={collateral?.ticketIncrement}
                step={collateral?.ticketIncrement}
                formatOptions={notionalFormatOptions}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.NOTIONAL?.negotiableFieldRequired) : deal.notional}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_DIRECTION)}
                readOnly={true}
                onUpdate={noop}
                value={getFormattedDirection(deal.direction, ownOrder, userPreference.directionFormat)}
              />
            </DialogField>
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_PRICING)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_RATE_VS_REFERENCE_RATE, {
                  referenceRate: referenceRate ? ReferenceRateHelper.getName(referenceRate) : null
                })}
                fieldName={NegotiationFieldMeta.RATE}
                validationField={DealFieldNames.DEAL_RATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getRateModificationType(negotiationFields?.RATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || !dealIsNotSbl}
                formatOptions={getRateFormatOptions(referenceRate)}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.RATE?.negotiableFieldRequired) : deal.rate}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_REFERENCE_RATE)}
                readOnly={true}
                onUpdate={noop}
                value={referenceRate?.name}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_HAIRCUT)}
                fieldName={NegotiationFieldMeta.HAIRCUT}
                validationField={DealFieldNames.DEAL_HAIRCUT}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getHaircutModificationType(negotiationFields?.HAIRCUT?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || isClearedDeal(dealDetail) || !dealIsNotSbl || dealIsGcPlus}
                min={0}
                formatOptions={getHaircutFormatOptions(undefined, userPreference.haircutSign, ownOrder, deal.direction)}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.HAIRCUT?.negotiableFieldRequired) : deal.haircut}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <HaircutTypeSelectboxField
                label={Locale.translate(Translation.TC_HAIRCUT_TYPE)}
                fieldName={NegotiationFieldMeta.HAIRCUT_TYPE}
                validationField={DealFieldNames.DEAL_HAIRCUT}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStringModificationType(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || !dealIsNotSbl || dealIsGcPlus}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.HAIRCUT_TYPE?.negotiableFieldRequired) : deal.haircutType}
                canClear={true}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_START_CONSIDERATION)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={
                  isAdminAmend
                    ? getNegotiatedValue(negotiationFields?.START_CONSIDERATION?.negotiableFieldRequired)
                    : deal.startConsideration
                }
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_END_CONSIDERATION)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={
                  isAdminAmend ? getNegotiatedValue(negotiationFields?.END_CONSIDERATION?.negotiableFieldRequired) : deal.endConsideration
                }
              />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_TERM)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_START_DATE)}
                fieldName={NegotiationFieldMeta.START_DATE}
                validationField={DealFieldNames.DEAL_START_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getStartDateModificationType(negotiationFields?.START_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable}
                dateFormat={dateEditorFormat}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.START_DATE?.negotiableFieldRequired) : deal.startDate}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                currency={currency}
                label={Locale.translate(Translation.TC_END_DATE)}
                fieldName={NegotiationFieldMeta.END_DATE}
                validationField={DealFieldNames.DEAL_END_DATE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getEndDateModificationType(negotiationFields?.END_DATE?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable || deal.repoClassification === RepoClassification.OPEN}
                dateFormat={dateEditorFormat}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.END_DATE?.negotiableFieldRequired) : deal.endDate}
              />
            </DialogField>
            <DialogField>
              <FilteredTermSelectboxField
                label={Locale.translate(Translation.TC_TERM)}
                fieldName={NegotiationFieldMeta.TERM}
                validationField={DealFieldNames.DEAL_TERM}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getTermModificationType(negotiationFields?.TERM?.negotiableFieldRequired),
                  dealDetail
                )}
                readOnly={!editable}
                onUpdate={onUpdateField}
                repoClassification={deal.repoClassification}
                value={term}
              />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_INSTRUMENT)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_ISIN_BUI)}
                readOnly={true}
                onUpdate={noop}
                value={getIsin(dealDetail)}
              />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CUSIP)} readOnly={true} onUpdate={noop} value={getCusip(dealDetail)} />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_ISSUER)} readOnly={true} onUpdate={noop} value={collateral?.issuer} />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_PRODUCT)} readOnly={true} onUpdate={noop} value={product?.name} />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_SECTOR)} readOnly={true} onUpdate={noop} value={productSector?.name} />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_REPO_TYPE)}
                readOnly={true}
                onUpdate={noop}
                value={formatRepoType(deal.repoType)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CURRENCY)} readOnly={true} onUpdate={noop} value={currency} />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_ACCRUED_INTEREST)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ minDp: 2 }}
                value={deal.accruedInterest}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COUPON)}
                readOnly={true}
                onUpdate={noop}
                formatOptions={{ percent: true }}
                value={collateral?.coupon}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_COLLATERAL_PRICE)}
                fieldName={NegotiationFieldMeta.BOND_PRICE}
                validationField={DealFieldNames.DEAL_BOND_PRICE}
                validationIssues={validationIssues}
                underline={getDealModificationColour(
                  () => getBondPriceModificationType(negotiationFields?.BOND_PRICE?.negotiableFieldRequired),
                  dealDetail
                )}
                min={0}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                maxLength={constants.bondPriceMaxFieldLength}
                readOnly={!editable || !!deal.basketAllocation}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(negotiationFields?.BOND_PRICE?.negotiableFieldRequired) : deal.bondPrice}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                readOnly={true}
                label={Locale.translate(Translation.TC_CLEAN_PRICE)}
                onUpdate={noop}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                value={deal.cleanPrice}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                readOnly={true}
                label={Locale.translate(Translation.TC_ALL_IN_PRICE)}
                onUpdate={noop}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                value={deal.allInPrice}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                readOnly={true}
                label={Locale.translate(Translation.TC_INDEX_RATIO)}
                onUpdate={noop}
                formatOptions={{
                  maxFractionDigits: constants.bondPriceMaxFractionDigits
                }}
                value={getDealIndexRatio({
                  collateral: dealDetail?.collateral,
                  deal
                })}
              />
            </DialogField>
            <DialogField />
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_PARTIES)}>
        <CollapseContentWrapper>
          <DialogRow>
            {cleared ? (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getTradingEntityName(dealDetail, false)}
                />
              </DialogField>
            ) : (
              <DialogField>
                {ownOrder ? (
                  <DealCounterpartyComboboxField
                    organisationId={counterparty?.organisationId ?? null}
                    isin={deal.collateralId}
                    fundId={deal.fundId}
                    marketType={deal.marketType}
                    usSponsoredClearing={usSponsoredClearing}
                    showAlias={true}
                    label={Locale.translate(Translation.TC_COUNTERPARTY)}
                    fieldName={NegotiationFieldMeta.COUNTERPARTY_ID}
                    validationField={DealFieldNames.DEAL_COUNTERPARTY_ID}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(negotiationFields?.COUNTERPARTY_ID?.negotiableFieldRequired),
                      dealDetail
                    )}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    value={
                      isAdminAmend ? getNegotiatedValue(negotiationFields?.COUNTERPARTY_ID?.negotiableFieldRequired) : counterparty?.id
                    }
                  />
                ) : (
                  <DealFundComboboxField
                    organisationId={orderFund?.organisationId ?? null}
                    isin={deal.collateralId}
                    marketType={deal.marketType}
                    counterpartyId={deal.counterpartyId}
                    usSponsoredClearing={usSponsoredClearing}
                    showAlias={true}
                    label={Locale.translate(Translation.TC_COUNTERPARTY)}
                    fieldName={NegotiationFieldMeta.FUND_ID}
                    validationField={DealFieldNames.DEAL_FUND_ID}
                    validationIssues={validationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(negotiationFields?.FUND_ID?.negotiableFieldRequired),
                      dealDetail
                    )}
                    readOnly={!editable}
                    onUpdate={onUpdateField}
                    value={isAdminAmend ? getNegotiatedValue(negotiationFields?.FUND_ID?.negotiableFieldRequired) : orderFund?.id}
                  />
                )}
              </DialogField>
            )}
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_COUNTERPARTY_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={isAdminAmend ? getServicingDealTradingEntityField(dealDetail, 'lei', false) : getTradingEntityLei(dealDetail, false)}
              />
            </DialogField>
            {cleared ? (
              <DealTicketSponsorField editable={editable} dealDetail={dealDetail} isAdminUser={isAdminUser} onUpdateField={onUpdateField} />
            ) : (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY_BIC)}
                  readOnly={true}
                  onUpdate={noop}
                  value={
                    isAdminAmend
                      ? getServicingDealTradingEntityField(dealDetail, 'bic', false)
                      : getTradingEntityField(dealDetail, 'bic', false)
                  }
                />
              </DialogField>
            )}
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_COUNTERPARTY_TRADER)}
                readOnly={true}
                onUpdate={noop}
                value={getTrader(dealDetail.deal.traderUserId, dealDetail.deal.counterpartyTraderId, ownOrder, false)}
              />
            </DialogField>
            {cleared ? (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_EXECUTING_COUNTERPARTY)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getExecutingTradingEntityName(dealDetail)}
                />
              </DialogField>
            ) : (
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_COUNTERPARTY_FIRM)}
                  readOnly={true}
                  onUpdate={noop}
                  value={getOrganisationField(dealDetail, 'legalName', false)}
                />
              </DialogField>
            )}
            {cleared ? (
              isAdminUser ? (
                <DealTicketExecutingCounterpartySponsorField dealDetail={dealDetail} />
              ) : (
                <DialogField />
              )
            ) : (
              <TextInputField
                label={Locale.translate(Translation.TC_COUNTERPARTY_FIRM_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={getOrganisationField(dealDetail, 'lei', false)}
              />
            )}
          </DialogRow>
          {isAdminUser && (
            <DialogRow>
              <DealTicketInvestmentDecisionMakerField
                dealDetail={dealDetail}
                self={false}
                isAdminUser={isAdminUser}
                onUpdateField={onUpdateField}
                readonly={!editable}
              />
              <DealTicketExecutionDecisionMakerField
                dealDetail={dealDetail}
                self={false}
                isAdminUser={isAdminUser}
                onUpdateField={onUpdateField}
                readonly={!editable}
              />
            </DialogRow>
          )}
          <DialogRow>
            <DialogField>
              {ownOrder ? (
                <DealFundComboboxField
                  organisationId={orderFund?.organisationId ?? null}
                  isin={deal.collateralId}
                  counterpartyId={deal.counterpartyId}
                  marketType={deal.marketType}
                  usSponsoredClearing={usSponsoredClearing}
                  label={Locale.translate(Translation.TC_MY_ENTITY)}
                  fieldName={NegotiationFieldMeta.FUND_ID}
                  validationField={DealFieldNames.DEAL_FUND_ID}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.FUND_ID?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable}
                  onUpdate={onUpdateField}
                  value={isAdminAmend ? getNegotiatedValue(negotiationFields?.FUND_ID?.negotiableFieldRequired) : orderFund?.id}
                />
              ) : (
                <DealCounterpartyComboboxField
                  organisationId={counterparty?.organisationId ?? null}
                  isin={deal.collateralId}
                  fundId={deal.fundId}
                  usSponsoredClearing={usSponsoredClearing}
                  marketType={deal.marketType}
                  label={Locale.translate(Translation.TC_MY_ENTITY)}
                  fieldName={NegotiationFieldMeta.COUNTERPARTY_ID}
                  validationField={DealFieldNames.DEAL_COUNTERPARTY_ID}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.COUNTERPARTY_ID?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable}
                  onUpdate={onUpdateField}
                  value={isAdminAmend ? getNegotiatedValue(negotiationFields?.COUNTERPARTY_ID?.negotiableFieldRequired) : counterparty?.id}
                />
              )}
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_ENTITY_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={isAdminAmend ? getServicingDealTradingEntityField(dealDetail, 'lei', true) : getTradingEntityLei(dealDetail, true)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_ENTITY_BIC)}
                readOnly={true}
                onUpdate={noop}
                value={
                  isAdminAmend
                    ? getServicingDealTradingEntityField(dealDetail, 'bic', true)
                    : getTradingEntityField(dealDetail, 'bic', true)
                }
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_TRADER)}
                readOnly={true}
                onUpdate={noop}
                value={getTrader(dealDetail.deal.traderUserId, dealDetail.deal.counterpartyTraderId, ownOrder, true)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_FIRM)}
                readOnly={true}
                onUpdate={noop}
                value={getOrganisationField(dealDetail, 'legalName', true)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MY_FIRM_LEI)}
                readOnly={true}
                onUpdate={noop}
                value={getOrganisationField(dealDetail, 'lei', true)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DealTicketInvestmentDecisionMakerField
              dealDetail={dealDetail}
              self={true}
              isAdminUser={isAdminUser}
              onUpdateField={onUpdateField}
              readonly={!editable}
            />
            <DealTicketExecutionDecisionMakerField
              dealDetail={dealDetail}
              self={true}
              isAdminUser={isAdminUser}
              onUpdateField={onUpdateField}
              readonly={!editable}
            />
            <DialogField>
              <MasterLegalAgreementTypeSelectboxField
                label={Locale.translate(Translation.TC_MASTER_LEGAL_AGREEMENT)}
                value={deal.masterLegalAgreementType}
                readOnly={true}
                onUpdate={noop}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_PRIMARY_SALES_COVER)}
                readOnly={true}
                onUpdate={noop}
                value={dealDetail.primaryCoverUser && UserHelper.getName(dealDetail.primaryCoverUser)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_SECONDARY_SALES_COVER)}
                readOnly={true}
                onUpdate={noop}
                value={dealDetail.secondaryCoverUser && UserHelper.getName(dealDetail.secondaryCoverUser)}
              />
            </DialogField>
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_FLAGS)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <AxeTradeConditionButtonsField
                label={Locale.translate(Translation.TC_AXE_ROLL_UNWIND)}
                readOnly={true}
                onUpdate={noop}
                isAxed={deal.isAxed}
                tradeConditionFlag={deal.tradeConditionFlag}
              />
            </DialogField>
            <DialogField>
              <CallableTypeSelectboxField
                label={Locale.translate(Translation.TC_CALLABLE)}
                readOnly={true}
                onUpdate={noop}
                value={deal.callableType}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                label={Locale.translate(Translation.TC_CALL_DAYS)}
                readOnly={true}
                onUpdate={noop}
                value={deal.callablePeriodDays}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_SETTLEMENT)}
                readOnly={true}
                onUpdate={noop}
                value={getSettlementVenueName(deal.settlementVenue)}
              />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_CSD)} readOnly={true} onUpdate={noop} value={deal.settlement} />
            </DialogField>
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>
      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_AUDIT)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_CREATION_TIMESTAMP)}
                readOnly={true}
                onUpdate={noop}
                value={Locale.formatTimestamp(deal.negotiationStarted, userPreference.dateFormat)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_DEAL_DATE)}
                readOnly={true}
                onUpdate={noop}
                value={Locale.formatDate(deal.createdAt, userPreference.dateFormat)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_DEAL_TIMESTAMP)}
                readOnly={true}
                onUpdate={noop}
                value={Locale.formatTime(deal.createdAt)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_LAST_UPDATED_AT)}
                readOnly={true}
                onUpdate={noop}
                value={Locale.formatTimestamp(deal.lastUpdatedAt, userPreference.dateFormat)}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_LAST_UPDATED_BY)}
                readOnly={true}
                onUpdate={noop}
                value={deal.lastUpdatedBy}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_ORIGINATOR)}
                readOnly={true}
                disabled={!ownOrder}
                onUpdate={noop}
                value={ownOrder ? deal.createdBy : Locale.translate(Translation.UC_NOT_APPLICABLE)}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_DEAL_ID)} readOnly={true} onUpdate={noop} value={deal.dealId} />
            </DialogField>
            <DialogField>
              <TextInputField label={Locale.translate(Translation.TC_RFQ_ID)} readOnly={true} onUpdate={noop} value={deal.orderId} />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_NEGOTIATION_ID)}
                readOnly={true}
                onUpdate={noop}
                value={deal.negotiationId}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_UTI)}
                readOnly={true}
                onUpdate={noop}
                value={isAdminUser || ownOrder ? deal.initiatorUtiRtn : deal.responderUtiRtn}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_MIC)}
                readOnly={true}
                onUpdate={noop}
                value={deal.marketIdentifierCode}
              />
            </DialogField>
            <DialogField />
          </DialogRow>
        </CollapseContentWrapper>
      </CollapsePanel>

      <CollapsePanel title={Locale.translate(Translation.TC_TICKET_INTERNAL_BOOKING)}>
        <CollapseContentWrapper>
          <DialogRow>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_BOOK)}
                fieldName={ownOrder ? NegotiationFieldMeta.INITIATOR_BOOK : NegotiationFieldMeta.RESPONDER_BOOK}
                validationField={ownOrder ? DealFieldNames.DEAL_INITIATOR_BOOK : DealFieldNames.DEAL_RESPONDER_BOOK}
                validationIssues={validationIssues}
                underline={getDealModificationColour(() => getStringModificationType(bookField), dealDetail)}
                readOnly={!editable}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(bookField) : ownOrder ? deal.initiatorBook : deal.responderBook}
                maxLength={constants.internalBookingMaxFieldLength}
              />
            </DialogField>
            <DialogField>
              <TextInputField
                label={Locale.translate(Translation.TC_STRATEGY)}
                fieldName={ownOrder ? NegotiationFieldMeta.INITIATOR_STRATEGY : NegotiationFieldMeta.RESPONDER_STRATEGY}
                validationField={ownOrder ? DealFieldNames.DEAL_INITIATOR_STRATEGY : DealFieldNames.DEAL_RESPONDER_STRATEGY}
                validationIssues={validationIssues}
                underline={getDealModificationColour(() => getStringModificationType(strategyField), dealDetail)}
                readOnly={!editable}
                onUpdate={onUpdateField}
                value={isAdminAmend ? getNegotiatedValue(strategyField) : ownOrder ? deal.initiatorStrategy : deal.responderStrategy}
                maxLength={constants.internalBookingMaxFieldLength}
              />
            </DialogField>
          </DialogRow>
          {ownOrder && (
            <DialogRow>
              <DialogField>
                <PrimeBrokerSelectboxField
                  label={Locale.translate(Translation.TC_PRIME_BROKER)}
                  fieldName={NegotiationFieldMeta.INITIATOR_PRIME_BROKER_ID}
                  validationField={DealFieldNames.DEAL_INITIATOR_PRIME_BROKER_ID}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.INITIATOR_PRIME_BROKER_ID?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable}
                  onUpdate={onUpdateField}
                  value={
                    isAdminAmend
                      ? getNegotiatedValue(negotiationFields?.INITIATOR_PRIME_BROKER_ID?.negotiableFieldRequired)
                      : deal.initiatorPrimeBrokerId
                  }
                  organisationId={deal.initiatorOrganisationId}
                />
              </DialogField>
              <DialogField>
                <TextInputField
                  label={Locale.translate(Translation.TC_BUNDLE)}
                  fieldName={NegotiationFieldMeta.BUNDLE}
                  validationField={DealFieldNames.DEAL_BUNDLE}
                  validationIssues={validationIssues}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.BUNDLE?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable}
                  onUpdate={onUpdateField}
                  value={isAdminAmend ? getNegotiatedValue(negotiationFields?.BUNDLE?.negotiableFieldRequired) : deal.bundle}
                  maxLength={constants.internalBookingMaxFieldLength}
                />
              </DialogField>
              <DialogField>
                <ToggleField
                  label={Locale.translate(Translation.TC_PRE_AGREED)}
                  fieldName={NegotiationFieldMeta.PRE_AGREED}
                  underline={getDealModificationColour(
                    () => getStringModificationType(negotiationFields?.PRE_AGREED?.negotiableFieldRequired),
                    dealDetail
                  )}
                  readOnly={!editable}
                  onUpdate={onUpdateField}
                  checked={isAdminAmend ? getNegotiatedValue(negotiationFields?.PRE_AGREED?.negotiableFieldRequired) : deal.preAgreed}
                  labelPosition={LabelPosition.Top}
                />
              </DialogField>
            </DialogRow>
          )}
        </CollapseContentWrapper>
      </CollapsePanel>
    </TicketFields>
  )
}

export const DealTicketFields = connect(mapStateToProps)(DealTicketFieldsComponent)
